/* eslint-disable max-len */
import _ from 'underscore';
import React from 'react';
import PropTypes from 'prop-types';
import { LayoutContext } from './layout-context';
import { layoutConfigs } from './layout-configs';
import { Mode } from './mode';
import { withLayoutContext } from './with-layout-context';

class LayoutHoCBase extends React.Component {
  static propTypes = {
    componentProps: PropTypes.shape({}).isRequired,
    changeLayout: PropTypes.func.isRequired,
    component: PropTypes.elementType.isRequired,
    toLayout: PropTypes.shape({}).isRequired,
    isGridLayout: PropTypes.bool.isRequired,
    layoutConfig: PropTypes.shape({
      menu: PropTypes.shape({
        mode: PropTypes.string,
      }).isRequired,
    }).isRequired,
  };

  constructor(props) {
    super(props);
    props.changeLayout(this.getTargetLayout(props.toLayout), true);
  }

  shouldComponentUpdate(nextProps) {
    const { component, componentProps } = nextProps;
    if (!_.isEqual(component, this.props.component) || !_.isEqual(componentProps, this.props.componentProps)) {
      this.props.changeLayout(this.getTargetLayout(nextProps.toLayout), true);
      return true;
    }

    return false;
  }

  getTargetLayout(toLayout) {
    if (!this.props.isGridLayout) {
      return toLayout;
    }

    const { layoutConfig } = this.props;

    if (layoutConfig && layoutConfig.menu && layoutConfig.menu.mode === Mode.Collapsed) {
      // if menu is collapsed (by user interaction), maintain the collapsed state
      return { ...toLayout, menu: { mode: Mode.Collapsed } };
    }

    return toLayout;
  }

  render() {
    const { componentProps, component: Component } = this.props;
    return <Component changeLayout={this.props.changeLayout} {...componentProps} />;
  }
}

const LayoutHoC = withLayoutContext(LayoutHoCBase);

export function withFullScreenLayout(Component) {
  return props => (
    <LayoutContext.Consumer>
      {({ change }) => (<LayoutHoC component={Component} toLayout={layoutConfigs.fullScreen} changeLayout={change} componentProps={props} />)}
    </LayoutContext.Consumer>
  );
}

export function withLayout(Component, toLayout) {
  return props => (
    <LayoutContext.Consumer>
      {({ change }) => (<LayoutHoC component={Component} toLayout={toLayout} changeLayout={change} componentProps={props} />)}
    </LayoutContext.Consumer>
  );
}

export function withMenuLayout(Component) {
  return props => (
    <LayoutContext.Consumer>
      {({ config }) => (<Component {...props} {...config.menu} />)}
    </LayoutContext.Consumer>
  );
}

export function withDefaultLayout(Component) {
  return props => (
    <LayoutContext.Consumer>
      {({ change }) => (<LayoutHoC component={Component} toLayout={{}} changeLayout={change} componentProps={props} />)}
    </LayoutContext.Consumer>
  );
}

export function withCollapsedMenuOnly(Component) {
  return props => (
    <LayoutContext.Consumer>
      {({ change }) => (<LayoutHoC component={Component} toLayout={layoutConfigs.withCollapsedMenuOnly} changeLayout={change} componentProps={props} />)}
    </LayoutContext.Consumer>
  );
}

export function withCollapsedMenuAndNav(Component) {
  return props => (
    <LayoutContext.Consumer>
      {({ change }) => (<LayoutHoC component={Component} toLayout={layoutConfigs.withCollapsedMenuAndNav} changeLayout={change} componentProps={props} />)}
    </LayoutContext.Consumer>
  );
}

export function withLayoutNavRefresh(Component, toLayout) {
  return props => (
    <LayoutContext.Consumer>
      {({ change, isGridLayout }) => {
        if (isGridLayout) {
          return (<LayoutHoC component={Component} toLayout={{ ...toLayout, nav: { mode: Mode.Expanded }, menu: { mode: Mode.Expanded } }} changeLayout={change} componentProps={props} />);
        }

        return (<LayoutHoC component={Component} toLayout={toLayout} changeLayout={change} componentProps={props} />);
      }}
    </LayoutContext.Consumer>
  );
}

export function withLayoutNavRefreshNoMenu(Component, toLayout) {
  return props => (
    <LayoutContext.Consumer>
      {({ change, isGridLayout }) => {
        if (isGridLayout) {
          return (<LayoutHoC component={Component} toLayout={{ ...toLayout, nav: { mode: Mode.Expanded } }} changeLayout={change} componentProps={props} />);
        }

        return (<LayoutHoC component={Component} toLayout={toLayout} changeLayout={change} componentProps={props} />);
      }}
    </LayoutContext.Consumer>
  );
}

export function withLayoutNavRefreshWithTab(Component, toLayout) {
  return props => (
    <LayoutContext.Consumer>
      {({ change, isGridLayout }) => {
        if (isGridLayout) {
          let { tab } = toLayout;
          if (tab.mode === Mode.Hidden) {
            tab = {
              ...toLayout.tab,
              mode: Mode.Collapsed,
              showDatePicker: false,
              singleTabMode: true,
            };
          }

          return (<LayoutHoC
            component={Component}
            toLayout={{
              ...toLayout,
              nav: { mode: Mode.Expanded },
              menu: { mode: Mode.Expanded },
              tab,
            }}
            changeLayout={change}
            componentProps={props}
          />);
        }

        return (<LayoutHoC component={Component} toLayout={toLayout} changeLayout={change} componentProps={props} />);
      }}
    </LayoutContext.Consumer>
  );
}

export const withMenuOnlyLayout = _.partial(withLayoutNavRefresh, _, layoutConfigs.withMenuOnly);
export const withoutMenuLayout = _.partial(withLayoutNavRefresh, _, layoutConfigs.withoutMenu);
export const withSingleTabLayout = _.partial(withLayoutNavRefresh, _, layoutConfigs.withSingleTab);
export const withSummaryBarAndTabNoDateLayout = _.partial(withLayout, _, { ...layoutConfigs.fullScreenWithSummaryBarAndTab, ...layoutConfigs.withoutDate });
export const withSummaryBarAndTabAndSubTabLayout = _.partial(withLayout, _, { ...layoutConfigs.fullScreenWithSummaryBarAndTab, ...layoutConfigs.withTabAndSubTab });
export const withFullScreenWithSummaryBar = _.partial(withLayout, _, layoutConfigs.fullScreenWithSummaryBar);
export const withFullScreenWithSummaryBarAndTabLayout = _.partial(withLayout, _, { ...layoutConfigs.fullScreenWithSummaryBarAndTab });
export const withMessageBarLayout = _.partial(withLayout, _, layoutConfigs.withMessageBar);

// *NavRefresh no L1, L2 in original shell, while adding L1, L2 for nav refresh

export function withFullScreenLayoutNavRefresh(Component) {
  return props => (
    <LayoutContext.Consumer>
      {({ change, isGridLayout }) => {
        if (isGridLayout) {
          return (<LayoutHoC component={Component} toLayout={layoutConfigs.fullScreenWithNavRefresh} changeLayout={change} componentProps={props} />);
        }

        return (<LayoutHoC component={Component} toLayout={layoutConfigs.fullScreen} changeLayout={change} componentProps={props} />);
      }}
    </LayoutContext.Consumer>
  );
}

export function withSummaryBarAndTabNoDateLayoutNavRefresh(Component) {
  return props => (
    <LayoutContext.Consumer>
      {({ change, isGridLayout }) => {
        if (isGridLayout) {
          return (<LayoutHoC component={Component} toLayout={{ ...layoutConfigs.staticComponents, ...layoutConfigs.withoutDate }} changeLayout={change} componentProps={props} />);
        }

        return (<LayoutHoC component={Component} toLayout={{ ...layoutConfigs.fullScreenWithSummaryBarAndTab, ...layoutConfigs.withoutDate }} changeLayout={change} componentProps={props} />);
      }}
    </LayoutContext.Consumer>
  );
}

export function withSummaryBarAndTabAndSubTabLayoutNavRefresh(Component) {
  return props => (
    <LayoutContext.Consumer>
      {({ change, isGridLayout }) => {
        if (isGridLayout) {
          return (<LayoutHoC component={Component} toLayout={{ ...layoutConfigs.staticComponents, ...layoutConfigs.withTabAndSubTab }} changeLayout={change} componentProps={props} />);
        }

        return (<LayoutHoC component={Component} toLayout={{ ...layoutConfigs.fullScreenWithSummaryBarAndTab, ...layoutConfigs.withTabAndSubTab }} changeLayout={change} componentProps={props} />);
      }}
    </LayoutContext.Consumer>
  );
}

export function withFullScreenWithSummaryBarNavRefresh(Component) {
  return props => (
    <LayoutContext.Consumer>
      {({ change, isGridLayout }) => {
        if (isGridLayout) {
          return (<LayoutHoC component={Component} toLayout={layoutConfigs.fullScreenWithSummaryBarWithNavRefresh} changeLayout={change} componentProps={props} />);
        }

        return (<LayoutHoC component={Component} toLayout={layoutConfigs.fullScreenWithSummaryBar} changeLayout={change} componentProps={props} />);
      }}
    </LayoutContext.Consumer>
  );
}

export function withFullScreenWithSummaryBarNavRefreshNoMenu(Component) {
  return props => (
    <LayoutContext.Consumer>
      {({ change, isGridLayout }) => {
        if (isGridLayout) {
          return (<LayoutHoC component={Component} toLayout={{ ...layoutConfigs.fullScreenWithSummaryBar, nav: { mode: Mode.Expanded } }} changeLayout={change} componentProps={props} />);
        }

        return (<LayoutHoC component={Component} toLayout={layoutConfigs.fullScreenWithSummaryBar} changeLayout={change} componentProps={props} />);
      }}
    </LayoutContext.Consumer>
  );
}

export function withFullScreenWithSummaryBarAndTabLayoutNavRefresh(Component) {
  return props => (
    <LayoutContext.Consumer>
      {({ change, isGridLayout }) => {
        if (isGridLayout) {
          return (<LayoutHoC component={Component} toLayout={layoutConfigs.staticComponents} changeLayout={change} componentProps={props} />);
        }

        return (<LayoutHoC component={Component} toLayout={layoutConfigs.fullScreenWithSummaryBarAndTab} changeLayout={change} componentProps={props} />);
      }}
    </LayoutContext.Consumer>
  );
}
